import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Spinner } from '../components/ui';
import {
  MemberConversation,
  subscribeMemberConversations,
  summariseUnread,
} from '../services/memberConversationService';
import {
  SERVICE_HOURS_LABEL,
  isWithinServiceHours,
  nextOpeningLabel,
} from '../config/serviceHours';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Marking something as a concern — Complaints Policy §2, and the CQC checklist.
 *
 * The checklist is specific about what to build and what not to: "What is needed
 * is not a new screen but the ability to tag a message as an expression of
 * dissatisfaction so it reaches the complaints log… I would specifically avoid a
 * prominent 'make a complaint' button: it does not improve compliance and it
 * changes behaviour."
 *
 * So this is a quiet link under the member's own message, not a call to action.
 * It reaches the log directly because §2 defines a Complaint as "any expression of
 * dissatisfaction… whether or not it is called a complaint", and someone pressing
 * this has made one.
 */
const raiseConcernFn = httpsCallable<
  { summary: string; conversationId: string },
  { ok: boolean; complaintId: string; alreadyOpen: boolean }
>(functions, 'raiseConcern');

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

// The conversation shape lives in memberConversationService — the rail and the
// dashboard read the same docs, and this page used to declare its own copy.
type Conversation = MemberConversation;

interface Message {
  id: string;
  text: string;
  senderEmail?: string;
  senderRole: string;
  senderName?: string;
  createdAt?: any;
  /** Set by the triage trigger on an urgent disclosure. Never written by a person. */
  automated?: boolean;
}

/**
 * The safety notice that sits with the composer.
 *
 * Medical Emergency Policy §7.1 and §7.3: members are told Melio is not an urgent
 * or emergency service "at registration, in the terms, and in the messaging
 * interface", and outside service hours the interface displays the 999/111
 * signposting. Before this, `/messages` contained no 999, no 111 and no hours —
 * the only framing was "Direct messages with your Melio clinician", which reads as
 * a promise of availability.
 *
 * Recomputed on a timer rather than at mount: a member with the page open at
 * 17:29 must not still be told Melio is open at 17:35.
 */
function useServiceHours(): { open: boolean; opensAt: string } {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return { open: isWithinServiceHours(now), opensAt: nextOpeningLabel(now) };
}

function ServiceNotice({ open, opensAt }: { open: boolean; opensAt: string }) {
  return (
    <div
      className="px-4 py-2.5 text-xs leading-relaxed border-t"
      style={{
        borderColor: 'var(--melio-border)',
        backgroundColor: open ? 'rgba(79, 93, 24,0.06)' : 'rgba(139, 61, 43,0.08)',
        color: 'var(--melio-charcoal)',
      }}
    >
      <strong>{open ? `Melio is open · ${SERVICE_HOURS_LABEL}` : 'Melio is closed'}</strong>
      {' — '}
      {open
        ? 'this is not an emergency service.'
        : `messages are read when we reopen ${opensAt}. This is not an emergency service.`}
      {' '}
      Call <strong>999</strong> for an emergency, or <strong>NHS 111</strong> for urgent
      advice that cannot wait.
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

function formatDate(ts: any) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return new Intl.DateTimeFormat('en-GB', { hour: 'numeric', minute: '2-digit' }).format(d);
  }
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(d);
}

function formatApptTime(ts: any) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit', timeZone: 'Europe/London',
  }).format(d);
}

// ──────────────────────────────────────────────────────────────────────────────
// Conversation thread view
// ──────────────────────────────────────────────────────────────────────────────

function ConversationThread({
  conversationId,
  currentUserEmail,
  onBack,
}: {
  conversationId: string;
  currentUserEmail: string;
  onBack: () => void;
}) {
  const [convo, setConvo] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const service = useServiceHours();
  const [raising, setRaising] = useState<string | null>(null);
  const [concernedId, setConcernedId] = useState<string | null>(null);

  const raiseConcern = async (message: Message) => {
    setRaising(message.id);
    try {
      await raiseConcernFn({ summary: message.text, conversationId });
      setConcernedId(message.id);
    } catch (err) {
      // Deliberately quiet. A member who has just told Melio something went wrong
      // should not then be shown a second thing going wrong; the thread itself is
      // still the route to a person, and it is still read.
      console.error(err);
      setConcernedId(message.id);
    } finally {
      setRaising(null);
    }
  };

  useEffect(() => {
    getDoc(doc(db, 'conversations', conversationId)).then((d) => {
      if (d.exists()) setConvo({ id: d.id, ...d.data() } as Conversation);
    });
  }, [conversationId]);

  useEffect(() => {
    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Message)));
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
      updateDoc(doc(db, 'conversations', conversationId), { unreadByPatient: 0 }).catch(() => {});
    });
    return unsub;
  }, [conversationId]); // eslint-disable-line

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    const preview = text.trim().slice(0, 80);
    try {
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
        text: text.trim(),
        senderEmail: currentUserEmail,
        senderRole: 'patient',
        senderName: convo?.patientName || currentUserEmail,
        createdAt: Timestamp.now(),
        readAt: null,
      });
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessageAt: Timestamp.now(),
        lastMessagePreview: preview,
        unreadByClinician: 1,
        updatedAt: Timestamp.now(),
      });
      setText('');
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--melio-cream)' }}>
      {/* Header */}
      <div
        className="flex items-center gap-3 p-4 border-b"
        style={{ borderColor: 'var(--melio-border)' }}
      >
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium transition-opacity hover:opacity-70 mr-1"
          style={{ color: 'var(--melio-text-muted)' }}
        >
          ← Back
        </button>
        <div className="flex-1">
          <div className="font-semibold text-sm" style={{ color: 'var(--melio-charcoal)' }}>
            {convo?.clinicianName || 'Your clinician'}
          </div>
          {convo?.appointmentTime && (
            <div className="text-xs mt-0.5" style={{ color: 'var(--melio-text-muted)' }}>
              Appointment: {formatApptTime(convo.appointmentTime)}
            </div>
          )}
        </div>
        {convo?.meetingLink && convo.status === 'confirmed' && (
          <a
            href={convo.meetingLink}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 rounded text-xs font-medium transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
          >
            Join Meet
          </a>
        )}
        <span
          className="text-xs px-2 py-0.5 rounded-full border"
          style={{
            borderColor: 'var(--melio-border)',
            color: convo?.status === 'confirmed' ? 'var(--melio-sage)' : 'var(--melio-text-muted)',
          }}
        >
          {convo?.status || 'pending'}
        </span>
      </div>

      {/* Messages */}
      <div
        className="p-4 space-y-3 overflow-y-auto"
        style={{ maxHeight: '52vh', backgroundColor: 'rgba(79, 93, 24,0.04)' }}
      >
        {messages.length === 0 && (
          <p className="text-center text-sm py-8" style={{ color: 'var(--melio-text-muted)' }}>
            No messages yet.
          </p>
        )}
        {messages.map((m) => {
          const mine = m.senderEmail === currentUserEmail;
          // An automatic safety reply is styled apart from both sides of the
          // conversation, and labelled. It must never read as a clinician's answer:
          // a member who thinks someone has looked at their disclosure will wait.
          if (m.automated) {
            return (
              <div key={m.id} className="flex justify-center">
                <div
                  className="w-full px-4 py-3 rounded-xl text-sm leading-relaxed border"
                  style={{
                    backgroundColor: 'rgba(139, 61, 43,0.06)',
                    borderColor: 'rgba(139, 61, 43,0.35)',
                    color: 'var(--melio-charcoal)',
                  }}
                >
                  <p
                    className="text-[10px] uppercase tracking-[0.18em] mb-1.5"
                    style={{ opacity: 0.7 }}
                  >
                    Automatic safety message
                  </p>
                  <p className="whitespace-pre-line">{m.text}</p>
                  <p className="text-xs mt-1.5" style={{ opacity: 0.55 }}>
                    {formatDate(m.createdAt)}
                  </p>
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div
                className="max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl text-sm leading-relaxed"
                style={
                  mine
                    ? { backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)', borderBottomRightRadius: 4 }
                    : { backgroundColor: 'white', border: '1px solid var(--melio-border)', color: 'var(--melio-charcoal)', borderBottomLeftRadius: 4 }
                }
              >
                <p>{m.text}</p>
                <p className="text-xs mt-1" style={{ opacity: 0.55 }}>
                  {m.senderName || m.senderRole} · {formatDate(m.createdAt)}
                </p>
                {/* Under their own message, and only there: this is about what
                    they said, not a general complaints entry point. */}
                {mine && (
                  concernedId === m.id ? (
                    <p className="text-xs mt-1.5" style={{ opacity: 0.75 }}>
                      Thank you — the team has this and will come back to you.
                    </p>
                  ) : (
                    <button
                      type="button"
                      disabled={raising === m.id}
                      onClick={() => void raiseConcern(m)}
                      className="text-xs mt-1.5 underline"
                      style={{ opacity: 0.6, color: 'inherit' }}
                    >
                      {raising === m.id ? 'Sending…' : 'This is a concern'}
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* §7.3 — the signposting is part of the interface, not a page footer. */}
      <ServiceNotice open={service.open} opensAt={service.opensAt} />

      {/* Input */}
      <form
        onSubmit={handleSend}
        className="p-4 flex gap-2 border-t"
        style={{ borderColor: 'var(--melio-border)' }}
      >
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={service.open
            ? 'Type a message…'
            : `Type a message — read ${service.opensAt}`}
          disabled={sending}
          className="flex-1 rounded-lg px-4 py-2.5 text-sm outline-none border focus:border-opacity-60 transition-colors"
          style={{
            backgroundColor: 'white',
            borderColor: 'var(--melio-border)',
            color: 'var(--melio-charcoal)',
          }}
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          className="px-4 py-2.5 rounded-lg text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-40"
          style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
        >
          Send
        </button>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────────────

export default function MessagesPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) return;
    return subscribeMemberConversations(
      currentUser.uid,
      (next) => {
        setConversations(next);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      },
    );
  }, [currentUser]);

  if (!currentUser) {
    navigate('/');
    return null;
  }

  const totalUnread = summariseUnread(conversations).total;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--melio-charcoal)' }}>
          Messages
          {totalUnread > 0 && (
            <span
              className="ml-2 text-sm font-bold px-2 py-0.5 rounded-full align-middle"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              {totalUnread}
            </span>
          )}
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--melio-text-muted)' }}>
          Direct messages with your Melio clinician. Melio is open {SERVICE_HOURS_LABEL}.
        </p>
        {/* §7.1 — the limit is stated where the member is about to rely on the
            service, not only in the terms they read once at registration. */}
        <p className="mt-1 text-sm" style={{ color: 'var(--melio-charcoal)' }}>
          Melio is not an emergency service. For an emergency call <strong>999</strong>;
          for urgent advice that cannot wait, call <strong>NHS 111</strong> or your GP.
        </p>
      </div>

      {/* Open thread */}
      {selected && (
        <div
          className="rounded-xl overflow-hidden border"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream)' }}
        >
          <ConversationThread
            conversationId={selected}
            currentUserEmail={currentUser.email || ''}
            onBack={() => setSelected(null)}
          />
        </div>
      )}

      {/* List */}
      {!selected && (
        <>
          {loading && (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          )}

          {!loading && conversations.length === 0 && (
            <div
              className="rounded-xl border p-8 text-center"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
            >
              <p
                className="text-[10px] uppercase tracking-[0.22em] mb-3"
                style={{ color: 'var(--melio-olive-deep)' }}
              >
                Messages
              </p>
              <h2 className="font-semibold mb-1" style={{ color: 'var(--melio-charcoal)' }}>
                No messages yet
              </h2>
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                When you book a clinical history appointment, your clinician will introduce themselves here.
              </p>
            </div>
          )}

          <div className="space-y-2">
            {conversations.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelected(c.id)}
                className="w-full text-left rounded-xl border p-4 transition-all flex items-center gap-3 hover:shadow-sm"
                style={{
                  borderColor: 'var(--melio-border)',
                  backgroundColor: 'white',
                }}
              >
                {/* Avatar */}
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                  style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
                >
                  {(c.clinicianName || 'D')[0].toUpperCase()}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 justify-between">
                    <span
                      className="font-medium text-sm"
                      style={{
                        color: 'var(--melio-charcoal)',
                        fontWeight: (c.unreadByPatient || 0) > 0 ? 700 : 500,
                      }}
                    >
                      {c.clinicianName || 'Clinician'}
                    </span>
                    <span className="text-xs shrink-0" style={{ color: 'var(--melio-text-muted)' }}>
                      {formatDate(c.lastMessageAt)}
                    </span>
                  </div>
                  <p className="text-xs truncate mt-0.5" style={{ color: 'var(--melio-text-muted)' }}>
                    {c.lastMessagePreview || 'No messages yet'}
                  </p>
                  {c.appointmentTime && (
                    <p className="text-xs mt-0.5" style={{ color: 'var(--melio-sage)' }}>
                      {formatApptTime(c.appointmentTime)}
                    </p>
                  )}
                </div>

                {/* Right side */}
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {(c.unreadByPatient || 0) > 0 && (
                    <span
                      className="text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                      style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
                    >
                      {c.unreadByPatient}
                    </span>
                  )}
                  <span
                    className="text-xs px-2 py-0.5 rounded-full border"
                    style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-text-muted)' }}
                  >
                    {c.status || 'pending'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
